
import { Device, Consumer } from '../types';
import { getCityByDepartmentAndIndex, getCityByIndex, targetCities } from '../filterUtils';

// Convert smart meters to GIS device format
export const convertSmartMeterToDevice = (meter: any, department: string, index: number): Device => {
  // Use the department-specific coordinates
  const city = getCityByDepartmentAndIndex(department, index);
  
  let status: 'Healthy' | 'Warning' | 'Critical' | 'Inactive' | 'Offline';
  if (meter.status === 'Good' || meter.status === 'Healthy') {
    status = 'Healthy';
  } else if (meter.status === 'Warning') {
    status = 'Warning';
  } else if (meter.status === 'Critical' || meter.status === 'Alert') {
    status = 'Critical';
  } else {
    status = 'Offline';
  }
  
  return {
    id: meter.deviceId,
    name: `${department} Smart Meter ${meter.deviceId.slice(-4)}`,
    type: 'smartMeter',
    status,
    location: {
      lat: city.lat + (Math.random() * 0.05 - 0.025), // Add small random offset
      lng: city.lng + (Math.random() * 0.05 - 0.025), // Add small random offset
      address: city.name
    },
    department,
    lastActive: meter.lastCommunication || new Date().toISOString()
  };
};

// Convert gateways to GIS device format
export const convertGatewayToDevice = (gateway: any, department: string, index: number): Device => {
  const city = getCityByDepartmentAndIndex(department, index);
  
  let status: 'Healthy' | 'Warning' | 'Critical' | 'Inactive' | 'Offline';
  if (gateway.status === 'Good' || gateway.status === 'Healthy') {
    status = 'Healthy';
  } else if (gateway.status === 'Warning') {
    status = 'Warning';
  } else if (gateway.status === 'Critical' || gateway.status === 'Alert') {
    status = 'Critical';
  } else {
    status = 'Offline';
  }
  
  return {
    id: gateway.deviceId,
    name: `${department} Gateway ${gateway.deviceId.slice(-4)}`,
    type: 'gateway',
    status,
    location: {
      lat: city.lat + (Math.random() * 0.05 - 0.025), // Add small random offset
      lng: city.lng + (Math.random() * 0.05 - 0.025), // Add small random offset
      address: city.name
    },
    department,
    lastActive: gateway.lastCommunication || new Date().toISOString()
  };
};

// Convert voltage regulators to GIS device format
export const convertRegulatorToDevice = (regulator: any, index: number): Device => {
  // Use energy department but different index range 
  const city = getCityByDepartmentAndIndex('Energy', index + 7);
  
  let status: 'Healthy' | 'Warning' | 'Critical' | 'Inactive' | 'Offline';
  if (regulator.status === 'Good') {
    status = 'Healthy';
  } else if (regulator.status === 'Warning') {
    status = 'Warning';
  } else if (regulator.status === 'Critical' || regulator.status === 'Alert') {
    status = 'Critical';
  } else {
    status = 'Offline';
  }
  
  return {
    id: regulator.deviceId,
    name: `Voltage Regulator ${regulator.deviceId.slice(-4)}`,
    type: 'voltageRegulator',
    status,
    location: {
      lat: city.lat + (Math.random() * 0.05 - 0.025), // Add small random offset
      lng: city.lng + (Math.random() * 0.05 - 0.025), // Add small random offset
      address: city.name
    },
    department: 'Energy',
    lastActive: regulator.lastCommunication || new Date().toISOString()
  };
};

// Convert static devices to GIS format
export const convertStaticDevicesToGisDevices = (devices: any[], startIndex: number): Device[] => {
  return devices.map((device, idx) => {
    const city = getCityByIndex((startIndex + idx) % targetCities.length);
    
    let status: 'Healthy' | 'Warning' | 'Critical' | 'Inactive' | 'Offline';
    if (device.status === 'active') {
      status = 'Healthy';
    } else if (device.status === 'maintenance') {
      status = 'Warning';
    } else if (device.status === 'critical') {
      status = 'Critical';
    } else if (device.status === 'inactive') {
      status = 'Inactive';
    } else {
      status = 'Offline';
    }
    
    return {
      id: device.id,
      name: device.name,
      type: device.type === 'energy' ? 
            (device.category.includes('Smart Meter') ? 'smartMeter' : 
             device.category.includes('Gateway') ? 'gateway' : 
             device.category.includes('Regulator') ? 'voltageRegulator' : 'other') : 
            device.type,
      status,
      location: {
        lat: city.lat + (Math.random() * 0.05 - 0.025), // Add small random offset
        lng: city.lng + (Math.random() * 0.05 - 0.025), // Add small random offset
        address: city.name
      },
      department: device.department,
      lastActive: device.lastActive
    };
  });
};

// Convert consumer data to GIS consumer format
export const convertToConsumer = (consumer: any, index: number): Consumer => {
  // For consumers, distribute them evenly across all cities
  const city = getCityByIndex(index % targetCities.length);
  
  return {
    id: consumer.accountId || `CON-${Math.floor(Math.random() * 10000)}`,
    name: consumer.name,
    address: city.name,
    location: {
      lat: city.lat + (Math.random() * 0.03 - 0.015), // Add small random offset
      lng: city.lng + (Math.random() * 0.03 - 0.015), // Add small random offset
    },
    consumption: consumer.usage || consumer.consumption || 0,
    status: consumer.status?.toLowerCase() === 'active' ? 'active' : 
            (consumer.status?.toLowerCase() === 'inactive' ? 'inactive' : 'defaulter')
  };
};
